import { useBanks } from '@starsoft/common/api/banks';
import { AutocompleteMenuSelector } from '@starsoft/common/components/AutocompleteMenuSelector';
import { ErrorChip } from '@starsoft/common/components/ErrorChip';
import { FormGroup } from '@starsoft/common/components/FormGroup';
import {
  CreateUserPaymentDetailsInputDto,
  UpdateUserPaymentDetailsInputDto,
} from '@starsoft/common/dto';
import {
  useKycMeByOriginIdWithOrigin,
  useTranslation,
} from '@starsoft/common/hooks';
import { Bank } from '@starsoft/common/models/bank';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * This component renders a form section for selecting a Nigerian bank account.
 * It utilizes several hooks and components to manage state, fetch data, and display UI elements.
 */
export default function UserPaymentDetailsFormContentBankAccountDetailsNigerianBankAccountSelect() {
  // Translation hook for internationalization
  const { t } = useTranslation('common');

  // Hook to fetch KYC (Know Your Customer) data
  const { kyc, isPending: isPendingKyc } = useKycMeByOriginIdWithOrigin();

  // State to manage the bank name input
  const [name, setName] = useState<string>('');

  // React Hook Form context to manage form state
  const { setValue, watch } = useFormContext<
    CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto
  >();

  // Watching the 'bankCode' field in the form
  const bankCode: string = watch('bankCode');

  // Custom hook to fetch banks based on the current state
  const {
    banks,
    isLoading: isLoadingBanks,
    error: banksError,
    refetch: refetchBanks,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useBanks({
    name,
    countries: kyc?.origin?.countries?.map(country =>
      country?.country?.id?.toString(),
    ),
    enabled: !!kyc?.origin?.countries && kyc?.origin?.countries?.length > 0,
  });

  /**
   * Callback function to handle bank selection.
   * @param {Bank} data - The selected bank object.
   * Example: handleSelectBank({ code: '123', name: 'Bank of Nigeria' });
   */
  const handleSelectBank = useCallback(
    (data: Bank) => {
      setValue('bankCode', data.code);
    },
    [setValue],
  );

  /**
   * Callback function to handle changes in the search input.
   * @param {string} value - The current value of the search input.
   * Example: onSearchChange('First Bank');
   */
  const onSearchChange = useCallback((value: string) => {
    setName(value);
  }, []);

  // If there's an error fetching banks, display an error chip with a retry action
  if (banksError) {
    return <ErrorChip action={refetchBanks} />;
  }

  // Render the form group with an autocomplete selector for banks
  return (
    <FormGroup
      label={t('bank_select_label')}
      isLoading={isLoadingBanks || isPendingKyc}
    >
      <AutocompleteMenuSelector
        filterBy={['name']}
        onSearchChange={onSearchChange}
        options={banks}
        getOptionLabel={option => option.name}
        getOptionValue={option => option.code}
        value={bankCode}
        setValue={handleSelectBank}
        loading={isLoadingBanks || isPendingKyc}
        loadingMore={isFetchingNextPage}
        loadMore={fetchNextPage}
        hasNextPage={hasNextPage}
        isAsync
        hideSearch
      />
    </FormGroup>
  );
}
